import { API_BASE_URL, authFetchJson } from '../apiClient.ts';
import type { RelationshipState } from '../relationships/relationshipApi.ts';

export type CoverageLine =
  | 'GENERAL_LIABILITY'
  | 'PROPERTY'
  | 'COMMERCIAL_AUTO'
  | 'HIRED_NON_OWNED_AUTO'
  | 'WORKERS_COMP'
  | 'UMBRELLA_EXCESS'
  | 'PROFESSIONAL_LIABILITY'
  | 'CYBER'
  | 'DIRECTORS_OFFICERS'
  | 'EPLI'
  | 'CRIME'
  | 'INLAND_MARINE'
  | 'INTELLECTUAL_PROPERTY'
  | 'CREDIT'
  | 'FLOOD'
  | 'POLLUTION'
  | 'BUILDERS_RISK'
  | 'FOREIGN_TRAVEL'
  | 'FOREIGN_LIABILITY'
  | 'VOLUNTEER_ACCIDENT'
  | 'BOND'
  | 'LIQUOR_LIABILITY'
  | 'PACKAGE'
  | 'OTHER';

// Package first, since most small accounts renew as one; the rest in the
// opportunity profile's order and with its labels.
export const COVERAGE_LINES: { value: CoverageLine; label: string }[] = [
  { value: 'PACKAGE', label: 'Package' },
  { value: 'GENERAL_LIABILITY', label: 'General liability' },
  { value: 'PROPERTY', label: 'Property' },
  { value: 'COMMERCIAL_AUTO', label: 'Commercial auto' },
  { value: 'HIRED_NON_OWNED_AUTO', label: 'Hired/non-owned auto' },
  { value: 'WORKERS_COMP', label: 'Workers compensation' },
  { value: 'UMBRELLA_EXCESS', label: 'Umbrella/excess' },
  { value: 'PROFESSIONAL_LIABILITY', label: 'Professional liability' },
  { value: 'CYBER', label: 'Cyber' },
  { value: 'DIRECTORS_OFFICERS', label: 'D&O' },
  { value: 'EPLI', label: 'EPLI' },
  { value: 'CRIME', label: 'Crime' },
  { value: 'INLAND_MARINE', label: 'Inland/ocean marine' },
  { value: 'INTELLECTUAL_PROPERTY', label: 'Intellectual property' },
  { value: 'CREDIT', label: 'Credit insurance' },
  { value: 'FLOOD', label: 'Flood' },
  { value: 'POLLUTION', label: 'Pollution liability' },
  { value: 'BUILDERS_RISK', label: 'Builders risk' },
  { value: 'FOREIGN_TRAVEL', label: 'Foreign travel' },
  { value: 'FOREIGN_LIABILITY', label: 'Foreign liability' },
  { value: 'VOLUNTEER_ACCIDENT', label: 'Volunteer accident' },
  { value: 'BOND', label: 'Bond' },
  { value: 'LIQUOR_LIABILITY', label: 'Liquor liability' },
  { value: 'OTHER', label: 'Other' }
];

const labels = new Map(COVERAGE_LINES.map((line) => [line.value, line.label]));
export const coverageLabel = (line: CoverageLine) => labels.get(line) ?? line;

// The opportunity profile's coverage checkboxes, one line each.
export const PROFILE_COVERAGE_FLAGS: Record<string, CoverageLine> = {
  coverageGeneralLiability: 'GENERAL_LIABILITY',
  coverageProperty: 'PROPERTY',
  coverageCommercialAuto: 'COMMERCIAL_AUTO',
  coverageHiredNonOwnedAuto: 'HIRED_NON_OWNED_AUTO',
  coverageWorkersComp: 'WORKERS_COMP',
  coverageUmbrellaExcess: 'UMBRELLA_EXCESS',
  coverageProfessionalLiability: 'PROFESSIONAL_LIABILITY',
  coverageCyber: 'CYBER',
  coverageDO: 'DIRECTORS_OFFICERS',
  coverageEPLI: 'EPLI',
  coverageCrime: 'CRIME',
  coverageInlandMarine: 'INLAND_MARINE',
  coverageIntellectualProperty: 'INTELLECTUAL_PROPERTY',
  coverageCreditInsurance: 'CREDIT',
  coverageFlood: 'FLOOD',
  coveragePollution: 'POLLUTION',
  coverageBuildersRisk: 'BUILDERS_RISK',
  coverageForeignTravel: 'FOREIGN_TRAVEL',
  coverageForeignLiability: 'FOREIGN_LIABILITY',
  coverageVolunteerAccident: 'VOLUNTEER_ACCIDENT',
  coverageBond: 'BOND',
  coverageLiquorLiability: 'LIQUOR_LIABILITY'
};

export type Renewal = {
  id: number;
  contactId: number;
  contact: {
    id: number;
    firstName: string;
    lastName: string;
    title: string | null;
    company: { id: number; name: string } | null;
  };
  // The deal working this renewal, when there is one
  opportunityId: number | null;
  opportunity: { id: number; dealName: string | null } | null;
  coverage: CoverageLine;
  // A plain date, "2027-03-12". Never passed through new Date(), which would
  // read it as UTC midnight and show the day before west of Greenwich.
  renewalDate: string;
  // Days from today; negative once it has passed.
  daysOut: number;
  // More than 30 days past and not yet rolled forward or removed.
  lapsed: boolean;
  incumbentCarrier: string | null;
  estimatedPremium: number | null;
  notes: string | null;
};

// The warmest way in: the colleague who knows this person best.
export type RenewalPath = {
  user: { id: number; fullName: string };
  isYou: boolean;
  score: number;
  state: RelationshipState;
  lastExchangeAt: string | null;
  noExchangeYet: boolean;
};

// bestPath is null when nobody here has written to them yet.
export type UpcomingRenewal = Renewal & { bestPath: RenewalPath | null };

// The counts come from the API, so this page, the dashboard and the digest agree.
export type UpcomingRenewals = {
  total: number;
  // People renewing from today on
  accounts: number;
  // Those nobody here has written to yet
  unknownAccounts: number;
  rows: UpcomingRenewal[];
};

export const WINDOW_CHOICES = [30, 60, 90, 120, 180] as const;
export const DEFAULT_WINDOW = 120;

// Everything renewing within `within` days, nearest first. Unless includePast is
// false it also returns what has gone by, lapsed or not.
export const fetchUpcomingRenewals = async (
  within: number,
  includePast = true
): Promise<UpcomingRenewals> => {
  const past = includePast ? '' : '&past=false';
  const { json } = await authFetchJson(`${API_BASE_URL}/renewals/upcoming?within=${within}${past}`);
  return json as UpcomingRenewals;
};

export const rollRenewal = async (id: number): Promise<Renewal> => {
  const { json } = await authFetchJson(`${API_BASE_URL}/renewals/${id}/roll`, {
    method: 'POST'
  });
  return json as Renewal;
};

// A deal's lines on one date, for its contact. Lines already on file for that
// date are linked to the deal instead.
export const addRenewalLines = async (body: {
  opportunityId: number;
  coverages: CoverageLine[];
  renewalDate: string;
  incumbentCarrier?: string | null;
}): Promise<{ created: number; skipped: number }> => {
  const { json } = await authFetchJson(`${API_BASE_URL}/renewals/bulk`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
  return json as { created: number; skipped: number };
};

export type ImportField =
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'name'
  | 'company'
  | 'coverage'
  | 'renewalDate'
  | 'carrier'
  | 'premium'
  | 'notes';
export type ColumnMapping = Partial<Record<ImportField, string>>;
export type ImportOutcome = 'create' | 'update' | 'unchanged' | 'duplicate' | 'error';

export type RenewalImportRow = {
  line: number;
  // id is null for a contact the import creates
  contact: { id: number | null; name: string; email: string | null };
  companyName: string | null;
  coverage: CoverageLine;
  renewalDate: string | null;
  rolledFrom: string | null;
  lapsed: boolean;
  incumbentCarrier: string | null;
  estimatedPremium: number | null;
  notes: string | null;
  outcome: ImportOutcome;
  error: string | null;
  warnings: string[];
};

export type RenewalImportResult = {
  dryRun: boolean;
  headers: string[];
  mapping: ColumnMapping;
  // Required fields with no column yet; rows is empty until they are picked.
  // The contact is an email column, a name column, or first and last.
  missing: ('contact' | 'renewalDate')[];
  warnings: string[];
  rows: RenewalImportRow[];
  summary: Record<ImportOutcome, number> & {
    rows: number;
    newContacts: number;
    lapsed: number;
    rolled: number;
  };
};

// dryRun previews the file without writing anything.
export const importRenewals = async (
  file: File,
  options: { dryRun: boolean; rollLapsed: boolean; mapping?: ColumnMapping }
): Promise<RenewalImportResult> => {
  const body = new FormData();
  body.append('file', file);
  if (options.mapping) body.append('mapping', JSON.stringify(options.mapping));
  const query = new URLSearchParams({
    dryRun: String(options.dryRun),
    rollLapsed: String(options.rollLapsed)
  });
  const { json } = await authFetchJson(`${API_BASE_URL}/renewals/import?${query}`, {
    method: 'POST',
    body
  });
  return json as RenewalImportResult;
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const partsOf = (date: string) => {
  const [year, month, day] = date.split('-').map(Number);
  return { year, month, day };
};

// "Mar 12, 2027", or "Mar 12" without the year.
export const formatRenewalDate = (date: string, withYear = true) => {
  const { year, month, day } = partsOf(date);
  const short = `${MONTHS[month - 1].slice(0, 3)} ${day}`;
  return withYear ? `${short}, ${year}` : short;
};

export const weekdayOf = (date: string) => {
  const { year, month, day } = partsOf(date);
  return WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
};

export const dayOfMonth = (date: string) => partsOf(date).day;

// Groups rows by "2027-03", labelled "March 2027".
export const monthKey = (date: string) => date.slice(0, 7);
export const monthLabel = (key: string) => {
  const { year, month } = partsOf(`${key}-01`);
  return `${MONTHS[month - 1]} ${year}`;
};

export const formatDaysOut = (daysOut: number) => {
  if (daysOut === 0) return 'today';
  if (daysOut === 1) return 'tomorrow';
  if (daysOut === -1) return 'yesterday';
  return daysOut > 0 ? `in ${daysOut} days` : `${-daysOut} days ago`;
};

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export const formatPremium = (value: number | null) =>
  value === null ? null : currency.format(value);

// "20 accounts renew in the next 120 days. Nobody here knows 11 of them."
export const renewalHeadline = (
  { accounts, unknownAccounts }: UpcomingRenewals,
  within: number
) => {
  const renew = accounts === 1 ? '1 account renews' : `${accounts} accounts renew`;
  const nobody = !unknownAccounts
    ? 'Someone here knows every one.'
    : unknownAccounts === accounts
      ? `Nobody here knows ${accounts === 1 ? 'them' : 'any of them'}.`
      : `Nobody here knows ${unknownAccounts} of them.`;
  return `${renew} in the next ${within} days. ${nobody}`;
};
